import Axios, { AxiosRequestConfig } from 'axios';
import credentialsService from 'services/credentialsService';
import env from 'env';
import refreshTokenService from 'services/refreshTokenService';
import bus, { AuthEventTypes } from 'modules/bus';
import { isRetryableError, delay, getMaxRetryCount } from 'retryHelper';
import { shouldHandleForbiddenGlobally } from 'request-responses';
import { getApiAutorization } from 'utils';

const httpClient = Axios.create({
  baseURL: `${env.SERVER_ENDPOINT}/api/`,
});

const setToken = (config: AxiosRequestConfig) => {
  const { token } = credentialsService;

  if (token && config.method !== 'OPTIONS') {
    config.headers.Authorization = getApiAutorization(token);
  }
};

const refresh = async (config: AxiosRequestConfig) => {
  await new Promise<void>((resolve, reject) => {
    function onRefreshTokenSuccess() {
      setToken(config);
      removeHandlers();
      resolve();
    }

    function onRefreshTokenFail() {
      reject(Error('Could not refresh token'));
      removeHandlers();
    }

    const removeSuccessListener = bus.addEventListener(
      AuthEventTypes.REFRESH_TOKEN_SUCCESS,
      onRefreshTokenSuccess,
    );
    const removeFailListener = bus.addEventListener(
      AuthEventTypes.REFRESH_TOKEN_FAIL,
      onRefreshTokenFail,
    );

    function removeHandlers() {
      removeSuccessListener();
      removeFailListener();
    }

    setTimeout(() => {
      reject(Error('Could not refresh token'));
      removeHandlers();
      refreshTokenService.refreshingAtm = false;
    }, refreshTokenService.checkRefreshingTreshold);
  });
};

httpClient.interceptors.request.use(
  async function (config) {
    // Do something before request is sent
    // If the header does not contain the token and the url not public, redirect to login

    setToken(config);
    if (refreshTokenService.refreshingAtm) {
      await refresh(config);
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

httpClient.interceptors.response.use(null, async (error) => {
  const { response, config } = error;
  const errorCode = response?.data?.messageCode;

  if (response?.status === 403) {
    if (
      shouldHandleForbiddenGlobally(errorCode) &&
      !config?.__skipGlobalForbiddenHandling
    ) {
      bus.broadcastEvent(AuthEventTypes.FORBIDDEN_SIGNAL);
    }

    return Promise.reject(error);
  }

  if (response?.status === 401) {
    if (refreshTokenService.triedRefresh) {
      bus.broadcastEvent(AuthEventTypes.LOGOUT_SIGNAL);
      return Promise.reject(error);
    }

    if (refreshTokenService.refreshingAtm) {
      await refresh(config);
      return httpClient(config);
    }

    try {
      await refreshTokenService.refreshToken();
      return httpClient(config);
    } catch (err) {
      bus.broadcastEvent(AuthEventTypes.LOGOUT_SIGNAL);
      error.preventDefault = true;
    }
  }

  if (isRetryableError(error)) {
    config.__retryCount = config.__retryCount || 0;

    if (config.__retryCount < getMaxRetryCount(config)) {
      config.__retryCount += 1;

      await delay(config.__retryCount);

      return httpClient(config);
    }
  }

  return Promise.reject(error);
});

export default httpClient;
